import React, { useState, useEffect } from 'react';
import { accessibilityAPI } from '../services/api';
import './Accessibility.css';

const Accessibility = () => {
  const [templates, setTemplates] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({
    category: '',
    subcategory: '',
    feature_category: '',
    name_en: '',
    name_ar: '',
    description_en: '',
    description_ar: '',
    type: '',
    is_active: true
  });
  const [formErrors, setFormErrors] = useState({});
  // State to track which categories are expanded
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  // State to track which feature categories are expanded
  const [expandedFeatureCategories, setExpandedFeatureCategories] = useState(new Set());

  // Valid options for form fields
  const validCategories = ['mobility', 'visual', 'hearing/speech', 'intellectual'];
  const validTypes = ['subcategory', 'feature_category', 'feature'];

  // Fetch templates on component mount
  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      // Fetch the organized structure for display
      const response = await accessibilityAPI.getAllTemplates();
      // The response data is an object with categories as keys
      setTemplates(response.data.data || {});
      
      setError(null);
    } catch (err) {
      console.error('Error fetching accessibility templates:', err);
      setError('Failed to load accessibility templates: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const val = type === 'checkbox' ? checked : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: val
    }));
    
    // Clear error for this field if it exists
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
    
    // Reset feature_category when type changes
    if (name === 'type' && val !== 'feature') {
      setFormData(prev => ({
        ...prev,
        feature_category: ''
      }));
    }
    
    // Reset feature_category when category changes
    if (name === 'category') {
      setFormData(prev => ({
        ...prev,
        feature_category: ''
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.category) {
      errors.category = 'Category is required';
    } else if (!validCategories.includes(formData.category)) {
      errors.category = 'Invalid category';
    }
    
    if (!formData.name_en.trim()) {
      errors.name_en = 'Name (English) is required';
    }
    
    if (!formData.type) {
      errors.type = 'Type is required';
    } else if (!validTypes.includes(formData.type)) {
      errors.type = 'Invalid type';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      // Prepare data for submission - only send fields that have values
      const submitData = {};
      
      // Always send these fields
      submitData.category = formData.category;
      submitData.name_en = formData.name_en;
      submitData.type = formData.type;
      submitData.is_active = formData.is_active;
      
      // Conditionally send other fields if they have values
      if (formData.subcategory) submitData.subcategory = formData.subcategory;
      if (formData.feature_category) submitData.feature_category = formData.feature_category;
      if (formData.name_ar) submitData.name_ar = formData.name_ar;
      if (formData.description_en) submitData.description_en = formData.description_en;
      if (formData.description_ar) submitData.description_ar = formData.description_ar;
      
      if (editingTemplate) {
        // Update existing template
        await accessibilityAPI.updateTemplate(editingTemplate.id, submitData);
      } else {
        // Create new template
        await accessibilityAPI.createTemplate(submitData);
      }
      
      // Reset form and refresh data
      resetForm();
      fetchTemplates();
    } catch (err) {
      console.error('Error saving template:', err);
      setError('Failed to save template: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleEdit = async (templateId) => {
    try {
      // Fetch the individual template by ID
      const response = await accessibilityAPI.getTemplateById(templateId);
      const template = response.data.data;
      
      // Log for debugging ID collisions
      console.log(`Editing template ID ${templateId}:`, template);
      
      // Check if this might be an ID collision (feature category vs feature with same ID)
      if (template.type === 'feature' && template.feature_category) {
        console.warn(`Possible ID collision detected for ID ${templateId}. This appears to be a feature, not a feature category.`);
      }
      
      setEditingTemplate(template);
      setFormData({
        category: template.category || '',
        subcategory: template.subcategory || '',
        feature_category: template.feature_category || '',
        name_en: template.name_en || '',
        name_ar: template.name_ar || '',
        description_en: template.description_en || '',
        description_ar: template.description_ar || '',
        type: template.type || '',
        is_active: template.is_active !== undefined ? template.is_active : true
      });
      setShowForm(true);
      
      // Scroll to the form with a slight delay to ensure DOM is updated
      setTimeout(() => {
        const formElement = document.querySelector('.template-form-container');
        if (formElement) {
          formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          // Also focus the first input field for better UX
          const firstInput = formElement.querySelector('input, select, textarea');
          if (firstInput) {
            firstInput.focus();
          }
        }
      }, 150);
    } catch (err) {
      console.error('Error fetching template:', err);
      setError('Failed to load template for editing: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleDelete = async (templateId) => {
    if (window.confirm('Are you sure you want to delete this template? This action cannot be undone.')) {
      try {
        await accessibilityAPI.deleteTemplate(templateId);
        fetchTemplates(); // Refresh the list
      } catch (err) {
        console.error('Error deleting template:', err);
        setError('Failed to delete template: ' + (err.response?.data?.message || err.message));
      }
    }
  };

  const resetForm = () => {
    setEditingTemplate(null);
    setFormData({
      category: '',
      subcategory: '',
      feature_category: '',
      name_en: '',
      name_ar: '',
      description_en: '',
      description_ar: '',
      type: '',
      is_active: true
    });
    setFormErrors({});
    setShowForm(false);
  };

  // Toggle category expansion
  const toggleCategory = (category) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
      }
      return newSet;
    });
  };
  
  // Toggle feature category expansion
  const toggleFeatureCategory = (categoryId) => {
    setExpandedFeatureCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };
  
  // Get feature categories for the selected category
  const getFeatureCategories = () => {
    if (!formData.category || !templates[formData.category]) {
      return [];
    }
    
    const categoryData = templates[formData.category];
    if (!categoryData.feature_categories || !Array.isArray(categoryData.feature_categories)) {
      return [];
    }
    
    // Extract unique feature category names
    const featureCategories = categoryData.feature_categories
      .map(item => item.name_en)
      .filter((name, index, arr) => name && arr.indexOf(name) === index); // Remove duplicates
    
    return featureCategories;
  };

  return (
    <div className="accessibility-page">
      <div className="page-header">
        <h1>Accessibility</h1>
        <div className="header-actions">
          <button 
            className="btn btn-secondary" 
            onClick={fetchTemplates}
            disabled={loading}
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
          <button 
            className="btn btn-primary fit" 
            onClick={() => setShowForm(!showForm)}
          >
            {showForm ? 'Cancel' : '+ Add New'}
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          Error: {error}
        </div>
      )}

      {showForm && (
        <div className="template-form-container">
          <h2>{editingTemplate ? 'Edit' : 'Add New'}</h2>
          <form onSubmit={handleSubmit} className="template-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="category">Category*</label>
                <select
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  className={formErrors.category ? 'error' : ''}
                >
                  <option value="">Select a category</option>
                  {validCategories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
                {formErrors.category && (
                  <span className="error-text">{formErrors.category}</span>
                )}
              </div>
              
              <div className="form-group">
                <label htmlFor="type">Type*</label>
                <select
                  id="type"
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  className={formErrors.type ? 'error' : ''}
                >
                  <option value="">Select a type</option>
                  {validTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
                {formErrors.type && (
                  <span className="error-text">{formErrors.type}</span>
                )}
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name_en">Name (English)*</label>
                <input
                  type="text"
                  id="name_en"
                  name="name_en"
                  value={formData.name_en}
                  onChange={handleInputChange}
                  className={formErrors.name_en ? 'error' : ''}
                  placeholder="Enter template name"
                />
                {formErrors.name_en && (
                  <span className="error-text">{formErrors.name_en}</span>
                )}
              </div>
              
              <div className="form-group">
                <label htmlFor="name_ar">Name (Arabic)</label>
                <input
                  type="text"
                  id="name_ar"
                  name="name_ar"
                  value={formData.name_ar}
                  onChange={handleInputChange}
                  placeholder="أدخل اسم القالب"
                />
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="description_en">Description (English)</label>
                <textarea
                  id="description_en"
                  name="description_en"
                  value={formData.description_en}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Enter description"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="description_ar">Description (Arabic)</label>
                <textarea
                  id="description_ar"
                  name="description_ar"
                  value={formData.description_ar}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="أدخل الوصف"
                />
              </div>
            </div>
            {/* Only show feature category select when type is 'feature' */}
            {formData.type === 'feature' && (
              <div className="form-group">
                <label htmlFor="feature_category">Feature Category</label>
                <select
                  id="feature_category"
                  name="feature_category"
                  value={formData.feature_category}
                  onChange={handleInputChange}
                  className={formErrors.feature_category ? 'error' : ''}
                  disabled={!formData.category}
                >
                  <option value="">Select a feature category (optional)</option>
                  {getFeatureCategories().map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
                {formErrors.feature_category && (
                  <span className="error-text">{formErrors.feature_category}</span>
                )}
                {!formData.category && (
                  <small className="hint">Please select a category first</small>
                )}
              </div>
            )}
            
            <div className="form-group">
              <div className="checkbox-group">
                <label htmlFor="is_active">Active</label>
                <input
                  type="checkbox"
                  id="is_active"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={resetForm}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary">
                {editingTemplate ? 'Update' : 'Add'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="templates-list">
        <h2>Categories</h2>
        
        {loading ? (
          <div className="loading-message">Loading...</div>
        ) : Object.keys(templates).length > 0 ? (
          Object.entries(templates).map(([category, categoryData]) => (
            <div key={category} className="category-section">
              <div 
                className={`category-header ${expandedCategories.has(category) ? 'expanded' : ''}`}
                onClick={() => toggleCategory(category)}
              >
                <h3>{category.charAt(0).toUpperCase() + category.slice(1)}</h3>
                <span className="toggle-icon">
                  {expandedCategories.has(category) ? '▼' : '►'}
                </span>
              </div>
              
              {expandedCategories.has(category) && (
                <div className="category-content">
                  {Object.entries(categoryData).map(([type, items]) => {
                    // Special handling for feature_categories to show features within them
                    if (type === 'feature_categories' && Array.isArray(items)) {
                      return (
                        <div key={type} className="type-section">
                          <h4>Feature Categories</h4>
                          {items.length > 0 ? (
                            items.map((featureCategory, fcIndex) => {
                              const featureCategoryId = `${category}-${featureCategory.id}`;
                              return (
                                <div key={fcIndex} className="feature-category-wrapper">
                                  <div 
                                    className={`feature-category-header ${expandedFeatureCategories.has(featureCategoryId) ? 'expanded' : ''}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleFeatureCategory(featureCategoryId);
                                    }}
                                  >
                                    <div className="feature-category-info">
                                      <h5>{featureCategory.name_en}</h5>
                                      {featureCategory.name_ar && <p className="arabic-text">{featureCategory.name_ar}</p>}
                                    </div>
                                    <div className="feature-category-actions">
                                      <button 
                                        className="btn btn-small btn-edit"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          // Add a warning for potential ID collisions
                                          console.log(`Attempting to edit feature category with ID: ${featureCategory.id}`);
                                          handleEdit(featureCategory.id);
                                        }}
                                      >
                                        Edit
                                      </button>
                                      <button 
                                        className="btn btn-small btn-delete"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDelete(featureCategory.id);
                                        }}
                                      >
                                        Delete
                                      </button>
                                      <span className="toggle-icon">
                                        {expandedFeatureCategories.has(featureCategoryId) ? '▼' : '►'}
                                      </span>
                                    </div>
                                  </div>
                                  
                                  {expandedFeatureCategories.has(featureCategoryId) && (
                                    <div className="feature-category-content">
                                      {featureCategory.description_en && <p>{featureCategory.description_en}</p>}
                                      {featureCategory.description_ar && <p className="arabic-text">{featureCategory.description_ar}</p>}
                                      
                                      {/* Display features within this feature category */}
                                      {Array.isArray(featureCategory.features) && featureCategory.features.length > 0 ? (
                                        <div className="templates-grid">
                                          {featureCategory.features.map((feature, fIndex) => (
                                            <div key={feature.id || `${fcIndex}-${fIndex}`} className="template-card">
                                              <div className="template-info">
                                                <h6>{feature.name_en}</h6>
                                                {feature.name_ar && <p className="arabic-text">{feature.name_ar}</p>}
                                                {feature.description_en && <p>{feature.description_en}</p>}
                                                {feature.description_ar && <p className="arabic-text">{feature.description_ar}</p>}
                                              </div>
                                              <div className="template-actions">
                                                {feature.id && (
                                                  <>
                                                    <button 
                                                      className="btn btn-small btn-edit"
                                                      onClick={() => handleEdit(feature.id)}
                                                    >
                                                      Edit
                                                    </button>
                                                    <button 
                                                      className="btn btn-small btn-delete"
                                                      onClick={() => handleDelete(feature.id)}
                                                    >
                                                      Delete
                                                    </button>
                                                  </>
                                                )}
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      ) : (
                                        <p className="no-features">No features in this category.</p>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })
                          ) : (
                            <div className="no-templates">
                              <p>No feature categories found for {category}.</p>
                            </div>
                          )}
                        </div>
                      );
                    }
                    
                    // Regular handling for other types (subcategories, features)
                    return (
                      <div key={type} className="type-section">
                        <h4>{type.replace('_', ' ').charAt(0).toUpperCase() + type.replace('_', ' ').slice(1)}</h4>
                        <div className="templates-grid">
                          {Array.isArray(items) && items.length > 0 ? (
                            items.map((item, index) => (
                              <div key={item.id || index} className="template-card">
                                <div className="template-info">
                                  <h5>{item.name_en}</h5>
                                  {item.name_ar && <p className="arabic-text">{item.name_ar}</p>}
                                  {item.description_en && <p>{item.description_en}</p>}
                                  {item.description_ar && <p className="arabic-text">{item.description_ar}</p>}
                                </div>
                                <div className="template-actions">
                                  {item.id && (
                                    <>
                                      <button 
                                        className="btn btn-small btn-edit"
                                        onClick={() => handleEdit(item.id)}
                                      >
                                        Edit
                                      </button>
                                      <button 
                                        className="btn btn-small btn-delete"
                                        onClick={() => handleDelete(item.id)}
                                      >
                                        Delete
                                      </button>
                                    </>
                                  )}
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="no-templates">
                              <p>No {type} found for {category}.</p>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="no-templates">
            <p>No accessibility templates found.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Accessibility;